import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import GeckoRequest from 'services/gecko-request';
import './index.scss';
import { getFileType } from 'utilities/attachmentUtils';

const useAttachment = (attachment) => {
  const [url, setUrl] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if(typeof attachment === 'string') {
      setUrl(attachment);
      setContentType(getFileType(attachment));
      setFileName(attachment.split('/').pop());
      return;
    }
    setIsLoading(true);
    GeckoRequest.post('/message/get-attachment', {attachment})
      .then((response) => {
        const {url, contentType, fileName} = response;
        setUrl(url);
        setContentType(contentType);
        setFileName(fileName);
      })
      .catch(console.error)
      .finally(() => {
        setIsLoading(false);
      });
  }, [attachment]);

  return {
    url,
    contentType,
    fileName,
    isLoading
  }
}

const AttachmentPreview = (props) => {
  const {
    url,
    isLoading,
    contentType,
    fileName
  } = useAttachment(props.attachment);
  const [error, setError] = useState(null);

  const elementError = (e) => {
    setError(e);
  };

  const handleImageClick = useCallback(() => {
      if(!props.onClick) { return; }
      return props.onClick(url, contentType, fileName);
  }, [props.onClick, url, contentType, fileName]);

  const displayImageElement = () => (
    <div onClick={handleImageClick} className="AttachmentPreview-image">
      <img src={url} alt={fileName} onError={elementError} />
    </div>
  );

  const getDisplayElement = (contentType) => {
    if(!contentType) { return null; }
    const type = contentType.split('/')[0];
    switch (type) {
      case 'image':
        return displayImageElement()
      case 'audio':
        return (
          <audio
            src={url}
            preload="metadata"
            controls
            className="AttachmentPreview-audio"
          >
            Sorry but your browser does not support HTML5 audio tags,{' '}
            <a href={url}>download the audio instead</a>
          </audio>
        );
      case 'video':
        return (
          <video
            src={url}
            preload="metadata"
            controls
            className="AttachmentPreview-video"
          >
            Sorry but your browser does not support HTML5 video tags,{' '}
            <a href={url}>download the video instead</a>
          </video>
        );
      case 'file':
      default:
        return (
          <a
            href={url}
            className="AttachmentPreview-file"
            target="_blank"
          >
            {fileName}
          </a>
        );
    }
  };

  const style = {
    display: {
      width: props.width,
    },
  };

  if (!props.attachment) return null;

  if (isLoading === true) {
    return (
      <div style={style.display} className={`AttachmentPreview`}>
        <img
          src="/img/loading_ring.svg"
          alt="loading"
          height={50}
          width={50}
        ></img>
      </div>
    );
  }

  if (error !== null) {
    return (
      <div
        style={style.display}
        className={`AttachmentPreview AttachmentPreview-missing`}
      >
        <span>
          {' '}
          <i className="far fa-exclamation-triangle"></i>Looks like this
          attachment is no longer available
        </span>
      </div>
    );
  }

  return (
    <div
      style={style.display}
      className={`AttachmentPreview 
        ${props.onRemove ? 'AttachmentPreview-removable' : ''}
        ${props.onClick ? 'AttachmentPreview-clickable' : ''}
      `}
      onClick={props.onRemove ? props.onRemove : null}
    >
      {getDisplayElement(contentType)}
    </div>
  );
};

AttachmentPreview.defaultProps = {
  url: '',
  width: 150,
};

AttachmentPreview.propTypes = {
  attachment: PropTypes.string,
  width: PropTypes.number,
  onRemove: PropTypes.func,
  onClick: PropTypes.func,
};

export default AttachmentPreview;
